import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { APP_INITIALIZER, Injectable, NgModule, inject } from '@angular/core';
import { SessionService } from '@galaxy/core';
import { Requestor, StorageBackend } from '@openid/appauth';
import { AuthService, Browser } from 'ionic-appauth';
import { XhrSettings } from 'ionic-appauth/lib/cordova';
import { VCapacitorBrowser } from './auth/v-capacitor-browser';
import { firstValueFrom } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import AuthConfig from './auth.config';
import SecureStorageBackend from './auth/secure-storage';

@Injectable({
  providedIn: 'root',
})
export class NgHttpService implements Requestor {
  private readonly http = inject(HttpClient);

  public async xhr<T>(settings: XhrSettings): Promise<T> {
    if (!settings.method) {
      settings.method = 'GET';
    }

    switch (settings.method) {
      case 'GET':
        return firstValueFrom(this.http.get<T>(settings.url, { headers: this.getHeaders(settings.headers) }));
      case 'POST':
        return firstValueFrom(
          this.http.post<T>(settings.url, settings.data, { headers: this.getHeaders(settings.headers) }),
        );
      case 'PUT':
        return firstValueFrom(
          this.http.put<T>(settings.url, settings.data, { headers: this.getHeaders(settings.headers) }),
        );
      case 'DELETE':
        return firstValueFrom(this.http.delete<T>(settings.url, { headers: this.getHeaders(settings.headers) }));
    }
  }

  private getHeaders(headers): HttpHeaders {
    let httpHeaders: HttpHeaders = new HttpHeaders();

    if (headers !== undefined) {
      Object.keys(headers).forEach((key) => {
        httpHeaders = httpHeaders.append(key, headers[key]);
      });
    }

    return httpHeaders;
  }
}

export const HAS_LAUNCHED_KEY = 'has_launched';
export const LEGACY_HAS_LAUNCHED_KEY = 'CapacitorStorage.has_launched';

export async function clearStaleKeychainOnFreshInstall(storage: SecureStorageBackend): Promise<void> {
  try {
    const { value } = await Preferences.get({ key: HAS_LAUNCHED_KEY });
    if (value) return;

    // Before Preferences was registered natively, this flag was written by
    // Capacitor's web fallback into localStorage. Finding it there means this
    // is an upgrade rather than a fresh install, so migrate the flag forward
    // and leave the Keychain alone -- clearing it would sign the user out.
    if (localStorage.getItem(LEGACY_HAS_LAUNCHED_KEY)) {
      await Preferences.set({ key: HAS_LAUNCHED_KEY, value: 'true' });
      return;
    }

    console.info('[Auth] Fresh install detected, clearing stale auth state');
    await storage.clear();
    await Preferences.set({ key: HAS_LAUNCHED_KEY, value: 'true' });
  } catch (e) {
    console.warn('[Auth] Failed to check fresh install status, skipping Keychain clear', e);
  }
}

@NgModule({
  imports: [CommonModule],
  providers: [
    {
      provide: StorageBackend,
      useFactory: () => new SecureStorageBackend(),
    },
    {
      provide: Requestor,
      useClass: NgHttpService,
    },
    {
      provide: Browser,
      useFactory: () => new VCapacitorBrowser(),
    },
    {
      provide: AuthService,
      useFactory: () => {
        const browser = inject(Browser);
        const storage = inject(StorageBackend);
        const requestor = inject(Requestor);
        const authService = new AuthService(browser, storage, requestor);
        authService.authConfig = inject(AuthConfig);
        return authService;
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const authService = inject(AuthService);
        const sessionService = inject(SessionService);
        const storage = inject(StorageBackend) as SecureStorageBackend;
        return async () => {
          if (Capacitor.isNativePlatform()) {
            await clearStaleKeychainOnFreshInstall(storage);
          }
          await authService.init();
          authService.token$.subscribe((token) => {
            if (Capacitor.isNativePlatform()) {
              sessionService.setSessionId(token?.accessToken);
            }
          });
        };
      },
      multi: true,
    },
  ],
})
export class AuthModule {}
